import * as Sentry from '@sentry/react'
import { useEffect, useMemo } from 'react'

import { usePageStateStore } from 'src/reducers'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { AuthContext } from './auth-context'

interface Props {
  children: React.ReactNode
}
export const AuthProvider = ({ children }: Props) => {
  const { authToken, clearAuthToken } = usePageStateStore()

  const {
    data: currentUser,
    isError,
    isLoading,
    isSuccess,
    failureReason,
  } = useCurrentUser(!!authToken)

  useEffect(() => {
    if (isError) {
      console.log('AuthProvider error:', failureReason)
      clearAuthToken()
    }
  }, [isError, failureReason, clearAuthToken])

  const isAuth = useMemo(
    () => isSuccess && !!currentUser,
    [isSuccess, currentUser]
  )

  useEffect(() => {
    if (!isAuth || !currentUser) {
      Sentry.setUser(null)
      return
    }

    Sentry.setUser({ id: currentUser.id, email: currentUser.email })

    try {
      window.clarity?.(
        'identify',
        currentUser.id,
        undefined,
        undefined,
        `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim()
      )
      window.clarity?.('set', 'email', currentUser.email)
    } catch (e) {
      Sentry.captureException(e)
    }
  }, [isAuth, currentUser])

  const user = isAuth ? (currentUser ?? null) : null

  return (
    <AuthContext.Provider
      value={{ loading: isLoading, isAuth, authError: isError, user }}
    >
      {children}
    </AuthContext.Provider>
  )
}
